// OrderSubmissionStatus - Shows order submission status with retry info
import { useState, useEffect } from 'react'
import { Clock, AlertCircle, RefreshCw } from 'lucide-react'
import type { Order } from '../auth/types'

interface Props {
  order: Order
  onRetry?: () => void
  isRetrying?: boolean
  isRetryInProgress?: boolean
}

export function OrderSubmissionStatus({ order, onRetry, isRetrying = false, isRetryInProgress = false }: Props) {
  // Re-render every 30s to keep "time ago" display fresh
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 30000)
    return () => clearInterval(timer)
  }, [])

  const submissionAttempts = order.submission_attempts || 0
  const maxRetries = 3
  const totalAttempts = maxRetries + 1 // initial + 3 retries = 4
  const hasSupplierOrderId = !!order.supplier_order_id
  const isAwaitingSupplier = order.status === 'started_processing' || order.status === 'awaiting_supplier'
  const isSubmissionFailed = order.status === 'submission_failed'
  const isProcessing = order.status === 'awaiting_supplier' ||
                       order.status === 'processing' ||
                       order.status === 'ready_to_collect' ||
                       order.status === 'out_for_delivery' ||
                       order.status === 'completed'
  const retriesExhausted = submissionAttempts >= totalAttempts
  // Can only retry if submission failed AND order wasn't successfully placed
  const canRetry = !hasSupplierOrderId && (isSubmissionFailed || (isAwaitingSupplier && retriesExhausted))

  // Detect a submission that's been waiting longer than the worst-case
  // legitimate retry gap (15 min cap on backoff). If the worker died during
  // a deploy and a scheduled retry was lost, the order sits in
  // awaiting_supplier forever; the 20-min threshold covers the longest
  // legitimate wait plus a small buffer.
  const STALE_THRESHOLD_MS = 20 * 60 * 1000
  const referenceTime = order.last_retry_at || order.created_at
  const isStuck = !!referenceTime &&
    (Date.now() - new Date(referenceTime).getTime() >= STALE_THRESHOLD_MS)

  // Format time ago
  const formatTimeAgo = (dateString: string | null | undefined): string => {
    if (!dateString) return ''

    const date = new Date(dateString)
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`
    return `${Math.floor(seconds / 86400)} days ago`
  }

  // Manual retry in progress — show processing state
  if (isRetryInProgress) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="bg-blue-50 px-3 sm:px-4 py-3 flex items-start gap-2">
          <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 text-sm">Retrying order submission</h3>
            <p className="text-xs sm:text-sm text-blue-700 mt-1">
              Submitting order to supplier, please wait...
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Failed state - retries exhausted
  if (canRetry) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Submission failed</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {submissionAttempts} attempt{submissionAttempts === 1 ? '' : 's'}
                {order.last_retry_at && ` · ${formatTimeAgo(order.last_retry_at)}`}
              </p>
            </div>
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="flex-shrink-0 inline-flex items-center gap-1.5 bg-red-600 text-white px-3 sm:px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Retrying' : 'Retry'}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Stuck state — no progress for over 20 min while still in
  // awaiting_supplier. Most likely cause: a scheduled retry was lost
  // (e.g. server redeploy during retry backoff). Surface a Try Again
  // button so the user can recover without waiting indefinitely.
  //
  // Gated on order.can_retry from the server: if the backend is
  // intentionally keeping the original task alive (e.g. Celery state
  // STARTED on a slow supplier API), can_retry is false and the
  // amber CTA stays hidden — otherwise the user would click a button
  // the API would reject with a 400. Falls back to the timestamp
  // check alone when can_retry is missing (older responses).
  const serverAllowsRetry = order.can_retry !== false
  if (!hasSupplierOrderId && isAwaitingSupplier && isStuck && !retriesExhausted && serverAllowsRetry) {
    return (
      <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white shadow-sm px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-3">
        <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900">Taking longer than expected</p>
          {order.last_retry_at && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              Last attempt: {formatTimeAgo(order.last_retry_at)}
            </p>
          )}
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="h-7 px-3 rounded-md bg-yellow-500 text-white text-[11px] font-medium hover:bg-yellow-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1 flex-shrink-0"
          >
            <RefreshCw className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Retrying' : 'Try again'}
          </button>
        )}
      </div>
    )
  }

  // In-progress state - still retrying (only if not yet placed)
  if (!hasSupplierOrderId && isAwaitingSupplier && submissionAttempts < totalAttempts) {
    const retryNumber = submissionAttempts
    const isInitialAttempt = submissionAttempts === 0
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="bg-blue-50 px-3 sm:px-4 py-3 flex items-start gap-2">
          <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 text-sm">Processing order</h3>
            <p className="text-xs sm:text-sm text-blue-700 mt-1">
              {isInitialAttempt
                ? 'Submitting order to supplier...'
                : `Retry ${retryNumber} of ${maxRetries} in progress...`
              }
            </p>
            {order.last_retry_at && (
              <p className="text-xs text-blue-600 mt-1">
                Last attempt: {formatTimeAgo(order.last_retry_at)}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Success state - order placed (has supplier_order_id or is in processing status)
  // Hide this since the status badge already shows the current status
  if (hasSupplierOrderId || isProcessing) {
    return null
  }

  // Default - no submission status to show
  return null
}
